import React from "react";
import {
  Button,
  Row,
  Col,
  Popover,
} from "antd";
import {
  PlaySquareOutlined,
  PauseOutlined,
  RollbackOutlined,
  FastForwardOutlined,
  SaveOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
} from "@ant-design/icons";

const HomeTrainerControls = ({
  // Control props
  isMobile = false,
  isConnected,
  simulate,
  pause,
  isAdmin,
  isSessionSaved,
  totTime,
  indexStep,
  steps,
  totTimeStep,
  onPause,
  onReset,
  onSkip,
  onSave,
  onSimulate,
  onShowTour,
  tourNudgeOpen = false,
  onDismissTourNudge,
  setIsSessionSaved,
  setSaveModalOpen,
  goNextStep,
  confirm,
  controlHeight = 64,
  controlHeightMobile = 52,
}) => {
  const height = isMobile ? controlHeightMobile : controlHeight;
  const iconBtnStyle = {
    height,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <Row
      gutter={isMobile ? 0 : 6}
      align="middle"
      wrap={false}
      style={{ width: "100%" }}
    >
      {/* Play */}
      <Col flex="1 1 0">
        <Button
          id="tour-play"
          block
          style={iconBtnStyle}
          disabled={!pause || (!isConnected && !simulate)}
          onClick={() => {
            onPause(false);
            setIsSessionSaved(false);
          }}
          type="primary"
        >
          <PlaySquareOutlined />
        </Button>
      </Col>

      {/* Pause */}
      <Col flex="1 1 0">
        <Button
          id="tour-pause"
          block
          style={iconBtnStyle}
          disabled={pause || (!isConnected && !simulate)}
          onClick={() => onPause(true)}
          type="primary"
        >
          <PauseOutlined />
        </Button>
      </Col>

      {/* Reset */}
      <Col flex="1 1 0">
        <Button
          id="tour-reset"
          block
          style={iconBtnStyle}
          disabled={!pause || (!isConnected && !simulate)}
          onClick={async () => {
            if (await confirm("This will reset your training")) {
              onReset();
              setIsSessionSaved(false);
            }
          }}
          type="default"
          danger
        >
          <RollbackOutlined />
        </Button>
      </Col>

      {/* Skip */}
      <Col flex="1 1 0">
        <Popover content="Skip current interval step. This can NOT be undone." disabled={isMobile}>
          <Button
            id="tour-skip"
            block
            style={iconBtnStyle}
            danger
            disabled={indexStep >= steps.length || steps[indexStep].duration - totTimeStep <= 2 || (!isConnected && !simulate)}
            onClick={() => goNextStep()}
            type="primary"
          >
            <FastForwardOutlined />
          </Button>
        </Popover>
      </Col>

      {/* Save */}
      <Col flex="1 1 0">
        <Button
          id="tour-save"
          block
          style={{
            ...iconBtnStyle,
            color: isSessionSaved ? '#52c41a' : undefined,
            borderColor: isSessionSaved ? '#52c41a' : undefined
          }}
          disabled={!pause || (!isConnected && !simulate) || totTime === 0}
          title={`Disabled: ${!pause ? 'NOT PAUSED' : (!isConnected && !simulate) ? 'NOT CONNECTED' : totTime === 0 ? 'NO TIME' : 'ENABLED'}`}
          onClick={() => {
            console.log('Save button clicked - opening modal');
            setSaveModalOpen(true);
          }}
          type="default"
        >
          <SaveOutlined />
        </Button>
      </Col>

      {/* Simulate (admin only) */}
      {isAdmin && (
        <Col flex="1 1 0">
          <Button
            id="tour-simulate"
            block
            style={iconBtnStyle}
            onClick={() => onSimulate?.(true)}
            disabled={isConnected || simulate}
            title="Simulate trainer"
            aria-label="Simulate trainer"
          >
            <RobotOutlined />
          </Button>
        </Col>
      )}

      {/* Help / Tour (non-admin only, desktop) */}
      {!isAdmin && !isMobile && (
        <Col flex="1 1 0">
          <Popover
            open={!!tourNudgeOpen}
            placement="top"
            overlayInnerStyle={{ border: "2px solid #ff4d4f", borderRadius: 10 }}
            content={
              <div style={{ maxWidth: 260 }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>
                  New here? Take the 60‑second tour.
                </div>
                <div style={{ marginBottom: 10 }}>
                  Click this button to learn what each control does.
                </div>
                <Button size="small" onClick={() => onDismissTourNudge?.()}>
                  Discard
                </Button>
              </div>
            }
          >
            <Button
              id="tour-help"
              block
              style={{
                ...iconBtnStyle,
                ...(tourNudgeOpen
                  ? { borderColor: "#ff4d4f" }
                  : null),
              }}
              onClick={() => onShowTour?.()}
              title="Show me a tour"
              aria-label="Show me a tour"
            >
              <QuestionCircleOutlined />
            </Button>
          </Popover>
        </Col>
      )}
    </Row>
  );
};

export default HomeTrainerControls;
